import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Search, X, Mic, Trash2, RefreshCcw, Camera, ArrowRight } from 'lucide-react';
import { useApp } from '../../../context/AppContext';
import { Product } from '../../../types';
import ImageSearchModal from '../../ImageSearchModal';

interface FullScreenSearchOverlayProps {
  api: any;
}

export default function FullScreenSearchOverlay({ api }: FullScreenSearchOverlayProps) {
  const { allProducts } = useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const [mounted, setMounted] = useState(false);
  const [showImageSearch, setShowImageSearch] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (api.showSuggestions) {
      const t = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(t);
    }
  }, [api.showSuggestions]);

  useEffect(() => {
    if (api.showSuggestions) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [api.showSuggestions]);

  if (!api.showSuggestions || !mounted) return null;

  const POPULAR_SEARCHES = api.lang === 'en' 
    ? [
        { term: 'Saffron Glow Day Cream', hot: true },
        { term: 'Korean Snail Essence', hot: true },
        { term: 'Rose Gold Elixir Serum', hot: false },
        { term: 'Lipstick', hot: false },
        { term: 'Hydrating Jelly', hot: false },
        { term: 'Sunscreen Gel', hot: true }
      ]
    : [
        { term: 'স্যাফরন গ্লো ডে ক্রিম', hot: true },
        { term: 'কোরিয়ান স্নেল এসেন্স', hot: true },
        { term: 'রোজ গোল্ড এলিক্সির সিরাম', hot: false },
        { term: 'লিপস্টিক', hot: false },
        { term: 'হাইড্রেটিং জেলি', hot: false },
        { term: 'সানস্ক্রিন জেল', hot: true }
      ];

  const query = api.searchQuery.toLowerCase().trim();
  const autocompletePhrases = query
    ? allProducts
        .filter((p: Product) => 
          p.nameEn.toLowerCase().includes(query) || 
          p.nameBn.toLowerCase().includes(query) ||
          p.categoryEn.toLowerCase().includes(query) ||
          p.categoryBn.toLowerCase().includes(query)
        )
        .map((p: Product) => api.lang === 'en' ? p.nameEn.toLowerCase() : p.nameBn)
        .filter((value, index, self) => self.indexOf(value) === index)
        .slice(0, 6)
    : [];

  const handleClose = () => {
    api.setShowSuggestions(false);
    // Don't clear query on close so they can resume
  };

  const overlayContent = (
    <div className="fixed inset-0 z-[9999] bg-[#f4f4f4] dark:bg-[#08080A] flex flex-col font-sans animate-fade-in">
      {/* Header Bar */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-primary dark:bg-[#0f172a]">
        {/* Back Button */}
        <button
          type="button"
          onClick={handleClose}
          className="p-1 shrink-0 text-white dark:text-white"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>

        {/* Input Container */}
        <form 
          onSubmit={api.handleSubmit} 
          className="flex-1 flex items-center h-[42px] bg-white dark:bg-[#121215] rounded-md overflow-hidden shadow-sm"
        >
          <div className="flex items-center pl-3 pr-2 text-neutral-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={api.searchQuery}
            onChange={(e) => api.executeSearch(e.target.value)}
            placeholder={api.lang === 'en' ? 'Search here...' : 'খুঁজুন...'}
            className="flex-1 min-w-0 h-full text-[15px] text-neutral-800 dark:text-white bg-transparent outline-none placeholder-neutral-400"
          />
          <div className="flex items-center px-2">
            {api.searchQuery ? (
              <button
                type="button"
                onClick={() => {
                  api.executeSearch('');
                  inputRef.current?.focus();
                }}
                className="p-1 text-neutral-400 hover:text-neutral-600"
              >
                <X className="w-5 h-5" />
              </button>
            ) : (
              <button
                type="button"
                className="p-1 text-neutral-500"
              >
                <Mic className="w-5 h-5" />
              </button>
            )}
          </div>
        </form>

        {/* Search Submit Button */}
        <button
          type="button"
          onClick={api.handleSubmit}
          className="w-[42px] h-[42px] bg-[#111827] text-white rounded-md flex items-center justify-center shrink-0 active:scale-95 transition-transform"
        >
          <Search className="w-5 h-5" />
        </button>

        <button 
          type="button" 
          onClick={() => setShowImageSearch(true)}
          className="p-1 shrink-0 text-white dark:text-white hover:opacity-80 transition-opacity"
        >
          <Camera className="w-6 h-6" />
        </button>
      </div>

      <ImageSearchModal 
        isOpen={showImageSearch}
        onClose={() => setShowImageSearch(false)}
        onSearch={(text) => {
          setShowImageSearch(false);
          api.executeSearch(text);
        }}
      />

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto">
        {!query ? (
          /* Empty State - Popular & History */
          <div className="p-4 space-y-8 bg-white dark:bg-[#08080A] h-full">
            {/* Popular Right Now */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-[16px] font-medium text-neutral-800 dark:text-neutral-200">
                  {api.lang === 'en' ? 'Popular Right Now' : 'জনপ্রিয় সার্চ'}
                </h3>
                <button className="p-1 text-neutral-800 dark:text-neutral-200">
                  <RefreshCcw className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {POPULAR_SEARCHES.map((item) => (
                  <button
                    key={item.term}
                    type="button"
                    onClick={() => api.handleTagClick(item.term)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f4f4f4] dark:bg-[#1a1a1e] rounded-full text-[14px] text-neutral-800 dark:text-neutral-200 transition-colors"
                  >
                    {item.hot && <span className="text-orange-500 text-[14px]">🔥</span>}
                    {item.term}
                  </button>
                ))}
              </div>
            </div>

            {/* Search History */}
            {api.searchHistory.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-[16px] font-medium text-neutral-800 dark:text-neutral-200">
                    {api.lang === 'en' ? 'Search History' : 'সার্চ হিস্টরি'}
                  </h3>
                  <button
                    type="button"
                    onClick={api.clearHistory}
                    className="flex items-center gap-1.5 text-[14px] text-neutral-500 hover:text-neutral-800 transition-colors"
                  >
                    {api.lang === 'en' ? 'Clear All' : 'সব মুছুন'}
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {api.searchHistory.map((term: string) => (
                    <button
                      key={term}
                      type="button"
                      onClick={() => api.handleTagClick(term)}
                      className="px-3 py-1.5 bg-[#f4f4f4] dark:bg-[#1a1a1e] rounded-full text-[14px] text-neutral-800 dark:text-neutral-200 transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Suggestions and Matching Products State */
          <div className="flex flex-col pb-10">
            {/* Autocomplete phrases list (like the screenshot) */}
            {autocompletePhrases.length > 0 && (
              <div className="flex flex-col w-full bg-[#f4f4f4] dark:bg-[#08080A] gap-[1px] pt-1">
                {autocompletePhrases.map((phrase, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => api.handleTagClick(phrase)}
                    className="w-full flex items-center justify-between px-3 py-3 bg-white dark:bg-[#121215] hover:bg-neutral-50 transition-colors text-left"
                  >
                    <span className="text-[15px] text-neutral-700 dark:text-neutral-300">
                      {phrase}
                    </span>
                    <div className="w-5 h-5 rounded-full border border-neutral-300 dark:border-neutral-600 flex items-center justify-center text-neutral-300 dark:text-neutral-600">
                      <ArrowRight className="w-3 h-3" />
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Product Suggestions with Images (User Requested) */}
            {api.suggestions && api.suggestions.length > 0 && (
              <div className="mt-2">
                <div className="px-3 py-2 bg-white dark:bg-[#121215] border-b border-[#f4f4f4] dark:border-white/5">
                  <span className="text-[13px] font-bold text-neutral-500">
                    {api.lang === 'en' ? 'Suggested Products' : 'সাজেস্টেড প্রোডাক্টস'}
                  </span>
                </div>
                
                <div className="flex flex-col bg-[#f4f4f4] dark:bg-[#08080A] gap-[1px]">
                  {api.suggestions.map((product: Product) => {
                    const imageSrc = product.image || (product as any).images?.[0];
                    const name = api.lang === 'en' ? product.nameEn : product.nameBn;

                    return (
                      <div
                        key={product.id}
                        onClick={() => {
                          api.handleSuggestionClick(product);
                          handleClose();
                        }}
                        className="flex items-center gap-3 px-3 py-2 bg-white dark:bg-[#121215] cursor-pointer hover:bg-neutral-50 transition-colors"
                      >
                        <div className="w-12 h-12 rounded border border-neutral-200/60 dark:border-neutral-800 overflow-hidden shrink-0">
                          <img
                            src={imageSrc}
                            alt=""
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-[14px] text-neutral-700 dark:text-neutral-300 truncate">
                            {name}
                          </h4>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[14px] font-bold text-primary">
                              ৳{product.price}
                            </span>
                            {product.regularPrice && product.regularPrice > product.price && (
                              <span className="text-[12px] text-neutral-400 line-through decoration-rose-500">
                                ৳{product.regularPrice}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty State when searching but no matches */}
            {query && autocompletePhrases.length === 0 && api.suggestions.length === 0 && (
              <div className="p-8 flex flex-col items-center justify-center text-center mt-10 bg-white dark:bg-[#121215] h-full">
                <div className="w-16 h-16 bg-neutral-100 dark:bg-neutral-900 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-neutral-300 dark:text-neutral-600" />
                </div>
                <h3 className="text-[16px] font-medium text-neutral-800 dark:text-neutral-200 mb-1">
                  {api.lang === 'en' ? 'No results found' : 'কোনো ফলাফল পাওয়া যায়নি'}
                </h3>
                <p className="text-[14px] text-neutral-500 max-w-[250px]">
                  {api.lang === 'en' 
                    ? `We couldn't find anything matching "${query}"` 
                    : `"${query}" এর জন্য কোনো প্রোডাক্ট পাওয়া যায়নি`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
}
